<script lang="ts">import { onMount, createEventDispatcher } from 'svelte';
import { initDarkMode } from '../../js-v2/stores/dark-mode-stores';
import { setFlags, flagsStore } from '../../js-v2/stores/flag-stores';
import { getUrl } from '../../js-v2/urlUtils';
import { initCurrentUrl } from '../../js-v2/stores/url-stores';
import { initCartCount, initLoggedIn } from '../../js-v2/stores/user-state-stores';
import CashSans from './CashSans/CashSans.svelte';
import CtaSubnav from './CtaSubnav/CtaSubnav.svelte';
import CtaSubnavV1 from '../PublicWebNav/Header/CtaSubnav/CtaSubnav.svelte';
import GlobalNav from './GlobalNav/GlobalNav.svelte';
import ImageSubnavV1 from '../PublicWebNav/Header/ImageSubnav/ImageSubnav.svelte';
import PublicWebDesignSystemTokens from './PublicWebDesignSystemTokens/PublicWebDesignSystemTokens.svelte';
import SearchProvider from '../Search/SearchProvider.svelte';
import MainContentJumpLink from './shared/MainContentJumpLink/MainContentJumpLink.svelte';
import { initTracking } from '../../js-v2/tracking';
/* eslint-disable prefer-const */
let { dataJSON = undefined, imageSubnavJSON = undefined, ctaSubnavJSON = $bindable(undefined), flagsJSON = undefined, darkMode = false, themeColor = '#006aff', themeFocusColor = '#0055cc', zIndex = 1000, cartCount = $bindable(0), currentUrl = $bindable(getUrl()), loggedIn = $bindable(undefined), } = $props();
const cartCountStore = initCartCount(cartCount);
const currentUrlStore = initCurrentUrl(currentUrl);
const darkModeStore = initDarkMode(darkMode);
const loggedInStore = initLoggedIn(loggedIn);
const dispatch = createEventDispatcher();
setFlags(flagsJSON);
initTracking($flagsStore);
// setter for solidshop
export const setCartCount = (cartCountFromShop) => (cartCount = cartCountFromShop);
export const setCurrentUrl = (currentUrlFromShop) => (currentUrl = currentUrlFromShop);
export const setCtaSubnavJSON = (ctaSubnavJSONFromShop) => (ctaSubnavJSON = ctaSubnavJSONFromShop);
// fetchCartData() returns the fetch promise
// so it can be chained from outside the component
export function fetchCartData() {
    return fetch(data?.props?.cart?.props?.dataUrl)
        .then((response) => response.json())
        .then((data) => {
        cartCount = data.item_count;
    })
        .catch((error) => {
        console.error('PublicWebNav: Unable to fetch cart data', error);
    });
}
// fetchLoggedInState() returns the fetch promise
// so it can be chained from outside the component
export function fetchLoggedInState() {
    return fetch('/mp/status')
        .then((response) => {
        if (response.status === 200) {
            loggedIn = true;
        }
        // default loggedIn is false so we don't need an else case here
        return response.json();
    })
        .catch((error) => {
        console.error('PublicWebNav: Unable to fetch logged in state', error);
    });
}
// Global Nav props
const data = $derived(dataJSON ? JSON.parse(dataJSON) : {});
// Cta Subnav props
const ctaSubnavData = $derived(ctaSubnavJSON ? JSON.parse(ctaSubnavJSON) : undefined);
// Image Subnav props
const imageSubnavData = $derived(imageSubnavJSON ? JSON.parse(imageSubnavJSON) : undefined);
const xms2SearchPageUrl = $derived($flagsStore.get('xms2-search-page-url'));
const useCtaSubnavV2 = $derived(ctaSubnavData && ctaSubnavData.componentName?.endsWith('V2'));
// When props change, update the stores
$effect(() => {
    $loggedInStore = loggedIn;
});
$effect(() => {
    $cartCountStore = cartCount;
});
$effect(() => {
    $currentUrlStore = currentUrl;
});
$effect(() => {
    $darkModeStore = darkMode;
});
$effect(() => {
    if (data?.props?.cart?.props?.dataUrl && typeof window !== 'undefined') {
        fetchCartData();
    }
});
$effect(() => {
    if (loggedIn === undefined && typeof window !== 'undefined') {
        fetchLoggedInState();
    }
});
let bodyHasMaxWidth = $state(false); // bodyHasMaxWidth would be used to determine if the nav outer gutter should be applied by setting --dynamic-gutter
let mounted = $state(false);
onMount(() => {
    // Used in PWR for tracking
    dispatch('mount', { meta: data && data.meta });
    mounted = true;
    // If the page is rendered using xms, xms2, or solidshop and its in old grid, the body element would have has a max-width set
    // If the page is rendered using shop FE, the body element would not have a max-width property but it is set to a shop layout container. This container have a class name that starts with "shop-layout"
    // nav outer gutter is only needed for the page that does not  has a max-width set
    const bodyMaxWidth = getComputedStyle(document.body).getPropertyValue('max-width'); // find out if the page has a max-width set
    const isShopFe = document.querySelector('[class^="shop-layout"]'); // find out if the page is rendered using shop FE
    bodyHasMaxWidth = (bodyMaxWidth && bodyMaxWidth !== 'none') || Boolean(isShopFe); // if the page has a max-width set or is rendered using shop FE, bodyHasMaxWidth would be true
    // set --dynamic-gutter below if the page does not have a max width set
});
</script>

<div
  class="PublicWebNavV2"
  class:darkMode
  data-nav-v2
  style:--color-theme={themeColor}
  style:--color-theme-focus={themeFocusColor}
  style:z-index={zIndex}
  style:--dynamic-gutter={bodyHasMaxWidth ? '0px' : 'calc((100vw - var(--body-width-max) - var(--space-30)) / 2)'}
  data-mounted={mounted}
>
  <MainContentJumpLink />
  <SearchProvider options={{ searchPageUrl: xms2SearchPageUrl, isEnabled: data.props?.enableSearch }}>
    <GlobalNav
      id={data.contentfulEntryId}
      homeLink={data.props?.homeLink}
      primaryNavItems={data.props?.primaryNavItems}
      secondaryNavItems={data.props?.secondaryNavItems}
      cart={data.props?.cart}
      primaryCtaButton={data.props?.cta}
      secondaryCtaButton={data.props?.secondaryCta}
      ctaButtonStyleV1={ctaSubnavData && !useCtaSubnavV2}
    />

    {#if imageSubnavData}
      <ImageSubnavV1 navItems={imageSubnavData.navItems} />
    {/if}
    {#if ctaSubnavData}
      {#if useCtaSubnavV2}
        <CtaSubnav
          id={ctaSubnavData.contentfulEntryId}
          title={ctaSubnavData.props.title}
          navItems={ctaSubnavData.props.navItems}
          primaryCtaButton={ctaSubnavData.props.cta}
          secondaryCtaButton={ctaSubnavData.props.secondaryCta}
          onlyShowCtaWhenSticky={ctaSubnavData.props.onlyShowCtaWhenSticky}
          showCtaOnCtaPage={ctaSubnavData.props.showCtaOnCtaPage}
          disableStickyBehavior={ctaSubnavData.props.disableStickyBehavior}
          hiddenUntilScrolled={ctaSubnavData.props.hiddenUntilScrolled}
          reverseDesktopCtaButtons={ctaSubnavData.props.reverseDesktopCtaButtons}
          disableIconCtaOnMobile={ctaSubnavData.props.disableIconCtaOnMobile}
        />
      {:else}
        <CtaSubnavV1 data={ctaSubnavData} />
      {/if}
    {/if}
  </SearchProvider>
</div>

<CashSans />
<PublicWebDesignSystemTokens />

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.PublicWebNavV2 {
  position: relative;
  --color-theme: #006aff;
  --color-theme-focus: #05c;
  --color-text: var(--color-neutral-000, #000000);
  --color-background: var(--color-neutral-1000, #fff);
  --color-flyout-background: var(--color-neutral-1000, #fff);
  --color-scrim-background: rgba(242, 242, 242, 0.8);
  --color-border: var(--color-neutral-700, #d3d3d3);
  --color-highlight: var(--color-neutral-900, #f7f6f5);
  --color-gray: var(--color-neutral-400, #888888);
}
.PublicWebNavV2.darkMode {
  --color-text: var(--color-neutral-1000, #fff);
  --color-background: var(--color-neutral-000, #000000);
  --color-flyout-background: var(--color-neutral-100, #232425);
  --color-scrim-background: rgba(26, 26, 26, 0.8);
  --color-border: #333;
  --color-highlight: var(--color-neutral-100, #232425);
  --color-gray: #b3b3b3;
}
.PublicWebNavV2 :global(.hidden) {
  display: none !important;
}
.PublicWebNavV2 :global(.CtaSubnavDesktopNav .header) {
  padding-left: 32px;
  padding-right: 32px;
}
@media (min-width: 105rem) {
  .PublicWebNavV2 :global(.CtaSubnavDesktopNav .header) {
    --nav-outer-gutter-base: calc(var(--space-10) / 2);
    --nav-outer-gutter: max(var(--nav-outer-gutter-base), var(--dynamic-gutter));
    padding-left: calc(32px + var(--nav-outer-gutter)) !important;
    padding-right: calc(22px + var(--nav-outer-gutter)) !important;
  }
}</style>
